import React, { createContext, useContext, useState } from "react";
import { Supplier } from "../types/supplier";
import {
    createSupplier,
    deleteSupplierApi,
    getSuppliers,
    updateSupplierApi,
} from "../api/supplierApi";
import { handleWithAlert } from "../utils/handleWithAlert";

interface SupplierContextProps {
    suppliers: Supplier[];
    addSupplier: (supplier: Supplier) => Promise<void>;
    updateSupplier: (supplier: Supplier) => Promise<void>;
    deleteSupplier: (id: string) => Promise<void>;
    fetchSuppliers: () => Promise<void>;
}

const SupplierContext = createContext<SupplierContextProps | undefined>(undefined);

export const SupplierProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);

    const fetchSuppliers = async () => {
        const list = await handleWithAlert(
            () => getSuppliers(),
            { errorMessage: "Failed to fetch supplier list" }
        );
        if (list) {
            setSuppliers(list.map((s: any) => ({
                ...s,
                id: s._id || s.id
            })));
        }
    };

    const addSupplier = async (supplier: Supplier) => {
        const created = await handleWithAlert(
            () => createSupplier(supplier),
            {
                successMessage: "Supplier created successfully!",
                errorMessage: "Failed to create supplier",
                showSuccess: true,
            }
        );
        if (created) {
            fetchSuppliers();
        }
    };

    const updateSupplier = async (updatedSupplier: Supplier) => {
        const updated = await handleWithAlert(
            () => updateSupplierApi(updatedSupplier.id!, updatedSupplier),
            {
                successMessage: "Supplier updated successfully!",
                errorMessage: "Failed to update supplier",
                showSuccess: true,
            }
        );
        if (updated) {
            fetchSuppliers();
        }
    };

    const deleteSupplier = async (id: string) => {
        const deleted = await handleWithAlert(
            () => deleteSupplierApi(id),
            {
                successMessage: "Supplier deleted successfully!",
                errorMessage: "Failed to delete supplier",
                showSuccess: true,
            }
        );
        if (deleted !== null) {
            setSuppliers(prev => prev.filter(s => s.id !== id));
        }
    };

    return (
        <SupplierContext.Provider value={{
            suppliers,
            addSupplier,
            updateSupplier,
            deleteSupplier,
            fetchSuppliers
        }}>
            {children}
        </SupplierContext.Provider>
    );
};

export const useSupplier = () => {
    const context = useContext(SupplierContext);
    if (!context) throw new Error("useSupplier must be inside SupplierProvider");
    return context;
};
